import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, ArrowLeft, Trash2, Pencil } from "lucide-react";
import { adminApi, apiErr, money, imgSrc, uploadFileAdmin } from "@/lib/apiClient";
import { Logo, Loading, ImageUploader } from "@/components/shared";
import LocationPinMap from "@/components/LocationPinMap";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const TABS = ["Overview", "Analytics", "Settings", "Templates", "Submissions", "Verification", "Keywords", "Disputes", "Flags", "Categories", "Support", "Lookup", "Audit", "Merge"];

// which roles may see each tab — mirrors the backend doc-07 access matrix so we
// never render a tab whose API calls would 403.
const TAB_ROLES = {
  Overview: ["super_admin", "finance", "support"],
  Analytics: ["super_admin", "finance"],
  Settings: ["super_admin", "finance"],
  Templates: ["super_admin"],
  Submissions: ["super_admin"],
  Verification: ["super_admin"],
  Keywords: ["super_admin"],
  Disputes: ["super_admin", "finance", "support"],
  Flags: ["super_admin", "support"],
  Categories: ["super_admin"],
  Support: ["super_admin", "support"],
  Lookup: ["super_admin", "support"],
  Audit: ["super_admin"],
  Merge: ["super_admin"],
};

const AdminLogin = ({ onLogin }) => {
  const [email, setEmail] = useState("admin@referlinc.app");
  const [password, setPassword] = useState("");
  const submit = async () => {
    try {
      const { data } = await adminApi.post("/auth/admin/login", { email, password });
      localStorage.setItem("rl_admin_token", data.token);
      onLogin();
    } catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <div className="flex min-h-screen items-center justify-center px-4">
      <div className="w-full max-w-sm rounded-[16px] border border-border bg-card p-8">
        <div className="mb-4 flex items-center gap-2 text-brand-green"><ShieldCheck className="h-5 w-5" /><span className="font-black">Platform Admin</span></div>
        <Input className="mb-2" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-email" />
        <Input className="mb-3" type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-password" />
        <Button className="w-full bg-brand-green hover:bg-brand-green-dark" onClick={submit} data-testid="admin-login-btn">Sign in</Button>
      </div>
    </div>
  );
};

const Stat = ({ label, value, onClick, testid }) => {
  const classes = `rounded-[14px] border border-border bg-card p-5 text-left ${onClick ? "cursor-pointer transition-colors hover:border-brand-green hover:bg-brand-green/5" : ""}`;
  if (onClick) {
    return (
      <button type="button" className={classes} onClick={onClick} data-testid={testid}>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-green">{label}</p>
        <p className="mt-2 text-3xl font-black">{value}</p>
      </button>
    );
  }
  return (
    <div className={classes} data-testid={testid}>
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-green">{label}</p>
      <p className="mt-2 text-3xl font-black">{value}</p>
    </div>
  );
};

const Overview = ({ onNavigate }) => {
  const [d, setD] = useState(null);
  useEffect(() => { adminApi.get("/admin/overview").then(({ data }) => setD(data)); }, []);
  if (!d) return <Loading />;
  return (
    <div className="grid gap-4 sm:grid-cols-3">
      <Stat label="Users" value={d.users} onClick={() => onNavigate?.("Lookup", { focus: "users" })} testid="overview-users" />
      <Stat label="Companies" value={d.companies} onClick={() => onNavigate?.("Lookup", { focus: "branches" })} testid="overview-companies" />
      <Stat label="Branches" value={d.branches} onClick={() => onNavigate?.("Lookup", { focus: "branches" })} testid="overview-branches" />
      <Stat label="Active businesses" value={d.active_businesses} onClick={() => onNavigate?.("Lookup", { focus: "branches", verifiedOnly: true })} testid="overview-active" />
      <Stat label="Open disputes" value={d.open_disputes} onClick={() => onNavigate?.("Disputes")} testid="overview-disputes" />
      <Stat label="Pending verifications" value={d.pending_verifications} onClick={() => onNavigate?.("Verification")} testid="overview-verifications" />
      <Stat label="Pending payouts" value={money(d.pending_payouts_total_cents)} onClick={() => onNavigate?.("Analytics")} testid="overview-payouts" />
    </div>
  );
};

const fmtHours = (h) => {
  if (h === null || h === undefined) return "—";
  if (h < 48) return `${h}h`;
  return `${(h / 24).toFixed(1)}d`;
};

const ReferralsPerDay = ({ series }) => {
  if (!series || series.length === 0) return null;
  const max = Math.max(1, ...series.map((p) => p.count));
  return (
    <div className="rounded-[14px] border border-border bg-card p-5" data-testid="referrals-per-day">
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-green">Referrals / day (30d)</p>
      <div className="mt-4 flex h-32 items-end gap-[2px]">
        {series.map((p) => (
          <div key={p.date} className="group relative flex-1" title={`${p.date}: ${p.count}`}>
            <div className="w-full rounded-t-sm bg-brand-green/70 transition-colors group-hover:bg-brand-green" style={{ height: `${Math.max(2, (p.count / max) * 100)}%` }} />
          </div>
        ))}
      </div>
      <p className="mt-2 text-xs text-muted-foreground">{series[0].date} → {series[series.length - 1].date}</p>
    </div>
  );
};

const Analytics = ({ onNavigate }) => {
  const [d, setD] = useState(null);
  useEffect(() => { adminApi.get("/admin/analytics").then(({ data }) => setD(data)); }, []);
  if (!d) return <Loading />;
  return (
    <div className="flex flex-col gap-4">
      <div className="grid gap-4 sm:grid-cols-3">
        <Stat label="Referrals created" value={d.referrals_created} onClick={() => onNavigate?.("Lookup", { focus: "users" })} testid="analytics-referrals" />
        <Stat label="Link clicks" value={d.link_clicks} onClick={() => onNavigate?.("Lookup", { focus: "users" })} testid="analytics-clicks" />
        <Stat label="Claims" value={d.claims} onClick={() => onNavigate?.("Lookup", { focus: "users" })} testid="analytics-claims" />
        <Stat label="Claim rate" value={`${d.claim_rate_pct}%`} />
        <Stat label="Conversions" value={d.conversions} onClick={() => onNavigate?.("Lookup", { focus: "users" })} testid="analytics-conversions" />
        <Stat label="Conversion rate" value={`${d.conversion_rate_pct}%`} />
        <Stat label="Avg payout" value={money(d.avg_payout_cents)} onClick={() => onNavigate?.("Disputes")} testid="analytics-avg-payout" />
        <Stat label="Settled payouts" value={d.settled_payouts} onClick={() => onNavigate?.("Disputes")} testid="analytics-settled" />
        <Stat label="Avg time to payout" value={fmtHours(d.avg_time_to_payout_hours)} />
        <Stat label="Top-ups total" value={money(d.top_ups_total_cents)} onClick={() => onNavigate?.("Lookup", { focus: "branches" })} testid="analytics-topups" />
      </div>
      <ReferralsPerDay series={d.referrals_per_day} />
    </div>
  );
};

const FEEDBACK_SETTING_KEYS = new Set([
  "referral_feedback_points_chart",
  "referral_feedback_max_points_per_pair",
  "referral_feedback_decay",
  "referral_feedback_tier_thresholds",
  "referral_feedback_prompt_cap_per_day",
  "referral_feedback_incentive_text",
  "referral_feedback_incentive_value",
  "referral_feedback_delay_days",
]);

const ANSWER_LABELS = {
  1: "Yes, absolutely",
  2: "Haven't tried yet",
  3: "Not a great experience",
  4: "Not planning to try",
};

const TrustFeedbackSettings = ({ rows, onSaved }) => {
  const byKey = Object.fromEntries((rows || []).filter((r) => !r.category_override_for).map((r) => [r.key, r]));
  const chartRow = byKey.referral_feedback_points_chart;
  const chart = chartRow?.value || {};
  const [grid, setGrid] = useState(() => ({
    1: { ...(chart["1"] || {}) },
    2: { ...(chart["2"] || {}) },
    3: { ...(chart["3"] || {}) },
    4: { ...(chart["4"] || {}) },
  }));
  const [maxPair, setMaxPair] = useState(String(byKey.referral_feedback_max_points_per_pair?.value ?? 1));
  const [delayDays, setDelayDays] = useState(String(byKey.referral_feedback_delay_days?.value ?? 3));
  const [promptCap, setPromptCap] = useState(String(byKey.referral_feedback_prompt_cap_per_day?.value ?? 1));
  const [incentive, setIncentive] = useState(String(byKey.referral_feedback_incentive_text?.value ?? ""));
  const [incentiveVal, setIncentiveVal] = useState(String(byKey.referral_feedback_incentive_value?.value ?? ""));
  const decay = byKey.referral_feedback_decay?.value || {};
  const [decayMode, setDecayMode] = useState(decay.mode || "subtract");
  const [decayAmount, setDecayAmount] = useState(String(decay.amount ?? 1));
  const [decayFactor, setDecayFactor] = useState(String(decay.factor ?? 0.95));
  const [decayEvery, setDecayEvery] = useState(String(decay.every_days ?? 30));
  const [tiers, setTiers] = useState(() => JSON.stringify(byKey.referral_feedback_tier_thresholds?.value || [], null, 2));

  const setCell = (answer, bucket, val) => {
    setGrid((g) => ({ ...g, [answer]: { ...g[answer], [bucket]: val } }));
  };

  const saveKey = async (key, value) => {
    await adminApi.patch("/admin/settings", { key, value, category_override_for: null });
  };

  const saveAll = async () => {
    try {
      const chartValue = {};
      for (const a of [1, 2, 3, 4]) {
        chartValue[String(a)] = {
          "1": Number(grid[a]["1"] ?? 0),
          "2": Number(grid[a]["2"] ?? 0),
          "3plus": Number(grid[a]["3plus"] ?? 0),
        };
      }
      await saveKey("referral_feedback_points_chart", chartValue);
      await saveKey("referral_feedback_max_points_per_pair", Number(maxPair));
      await saveKey("referral_feedback_delay_days", Number(delayDays));
      await saveKey("referral_feedback_prompt_cap_per_day", Number(promptCap));
      await saveKey("referral_feedback_incentive_text", incentive);
      await saveKey("referral_feedback_incentive_value", incentiveVal);
      await saveKey("referral_feedback_decay", {
        mode: decayMode,
        amount: Number(decayAmount),
        factor: Number(decayFactor),
        every_days: Number(decayEvery),
      });
      await saveKey("referral_feedback_tier_thresholds", JSON.parse(tiers));
      toast.success("Trust feedback settings saved");
      onSaved?.();
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  if (!chartRow) {
    return (
      <p className="text-sm text-muted-foreground">
        Trust feedback settings will appear after the next backend deploy seeds them.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-4 rounded-[14px] border border-border bg-card p-5" data-testid="trust-feedback-settings">
      <div>
        <h3 className="text-sm font-black uppercase tracking-wide">Referral feedback & trust tiers</h3>
        <p className="text-sm text-muted-foreground">
          Points chart by answer and occurrence. Changes are audit-logged. Super admin and finance can edit.
        </p>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="feedback-points-grid">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
              <th className="pb-2 pr-3">Answer</th>
              <th className="pb-2 pr-3">1st</th>
              <th className="pb-2 pr-3">2nd</th>
              <th className="pb-2">3rd+</th>
            </tr>
          </thead>
          <tbody>
            {[1, 2, 3, 4].map((a) => (
              <tr key={a}>
                <td className="py-1.5 pr-3 font-medium">{a}. {ANSWER_LABELS[a]}</td>
                {["1", "2", "3plus"].map((b) => (
                  <td key={b} className="py-1.5 pr-3">
                    <Input
                      className="w-20"
                      type="number"
                      value={grid[a][b] ?? 0}
                      onChange={(e) => setCell(a, b, e.target.value)}
                      data-testid={`fb-pts-${a}-${b}`}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <label className="text-sm">
          <span className="text-xs font-bold uppercase tracking-wide text-brand-green">Max counted per referrer↔referred pair</span>
          <Input className="mt-1" type="number" value={maxPair} onChange={(e) => setMaxPair(e.target.value)} data-testid="fb-max-pair" />
        </label>
        <label className="text-sm">
          <span className="text-xs font-bold uppercase tracking-wide text-brand-green">Ask after N days</span>
          <Input className="mt-1" type="number" value={delayDays} onChange={(e) => setDelayDays(e.target.value)} data-testid="fb-delay-days" />
        </label>
        <label className="text-sm">
          <span className="text-xs font-bold uppercase tracking-wide text-brand-green">Login prompt cap / day</span>
          <Input className="mt-1" type="number" value={promptCap} onChange={(e) => setPromptCap(e.target.value)} data-testid="fb-prompt-cap" />
        </label>
        <label className="text-sm sm:col-span-2">
          <span className="text-xs font-bold uppercase tracking-wide text-brand-green">Incentive text</span>
          <Input className="mt-1" value={incentive} onChange={(e) => setIncentive(e.target.value)} data-testid="fb-incentive" />
        </label>
        <label className="text-sm">
          <span className="text-xs font-bold uppercase tracking-wide text-brand-green">Incentive value (optional)</span>
          <Input className="mt-1" value={incentiveVal} onChange={(e) => setIncentiveVal(e.target.value)} data-testid="fb-incentive-value" />
        </label>
      </div>

      <div className="grid gap-3 sm:grid-cols-4">
        <label className="text-sm">
          <span className="text-xs font-bold uppercase tracking-wide text-brand-green">Decay mode</span>
          <select className="mt-1 flex h-10 w-full rounded-md border border-border bg-background px-2 text-sm" value={decayMode} onChange={(e) => setDecayMode(e.target.value)} data-testid="fb-decay-mode">
            <option value="subtract">Subtract</option>
            <option value="multiply">Multiply</option>
          </select>
        </label>
        <label className="text-sm">
          <span className="text-xs font-bold uppercase tracking-wide text-brand-green">Amount</span>
          <Input className="mt-1" type="number" value={decayAmount} onChange={(e) => setDecayAmount(e.target.value)} data-testid="fb-decay-amount" />
        </label>
        <label className="text-sm">
          <span className="text-xs font-bold uppercase tracking-wide text-brand-green">Factor</span>
          <Input className="mt-1" type="number" step="0.01" value={decayFactor} onChange={(e) => setDecayFactor(e.target.value)} data-testid="fb-decay-factor" />
        </label>
        <label className="text-sm">
          <span className="text-xs font-bold uppercase tracking-wide text-brand-green">Every N days</span>
          <Input className="mt-1" type="number" value={decayEvery} onChange={(e) => setDecayEvery(e.target.value)} data-testid="fb-decay-every" />
        </label>
      </div>

      <label className="text-sm">
        <span className="text-xs font-bold uppercase tracking-wide text-brand-green">Tier thresholds (JSON)</span>
        <textarea className="mt-1 w-full rounded-md border border-border bg-background p-2 font-mono text-xs" rows={5} value={tiers} onChange={(e) => setTiers(e.target.value)} data-testid="fb-tiers-json" />
      </label>

      <Button className="w-fit bg-brand-green hover:bg-brand-green-dark" onClick={saveAll} data-testid="fb-settings-save">
        Save trust feedback settings
      </Button>
    </div>
  );
};

const Settings = () => {
  const [rows, setRows] = useState(null);
  const [locked, setLocked] = useState([]);
  const [overrideKeys, setOverrideKeys] = useState([]);
  const [cats, setCats] = useState([]);
  const [edits, setEdits] = useState({});
  const [ovKey, setOvKey] = useState("");
  const [ovCat, setOvCat] = useState("");
  const [ovVal, setOvVal] = useState("");
  const load = () => adminApi.get("/admin/settings").then(({ data }) => {
    setRows(data.settings); setLocked(data.locked_keys); setOverrideKeys(data.category_override_keys || []);
  });
  useEffect(() => { load(); adminApi.get("/categories").then(({ data }) => setCats(data)); }, []);
  const save = async (row) => {
    let value = edits[row.key];
    try { value = JSON.parse(value); } catch {}
    try {
      await adminApi.patch("/admin/settings", { key: row.key, value, category_override_for: row.category_override_for || null });
      toast.success("Updated"); load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const saveOverride = async (key, categoryId, rawVal) => {
    let value = rawVal;
    try { value = JSON.parse(rawVal); } catch {}
    try {
      await adminApi.patch("/admin/settings", { key, value, category_override_for: categoryId });
      toast.success("Override saved"); setOvVal(""); setOvKey(""); setOvCat(""); load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  if (!rows) return <Loading />;
  const catName = (id) => cats.find((c) => c.id === id)?.name || id;
  const overrides = rows.filter((r) => r.category_override_for);
  const generalRows = rows.filter((r) => !r.category_override_for && !FEEDBACK_SETTING_KEYS.has(r.key));
  return (
    <div className="flex flex-col gap-6">
      <TrustFeedbackSettings rows={rows} onSaved={load} />

      <div className="flex flex-col gap-2">
        <p className="text-sm text-muted-foreground">Every business rule value lives here — nothing is hardcoded. Values are JSON.</p>
        {generalRows.map((r) => {
          const isLocked = locked.includes(r.key);
          return (
            <div key={r.key} className="flex flex-wrap items-center gap-3 rounded-[12px] border border-border bg-card px-4 py-3" data-testid={`setting-${r.key}`}>
              <span className="w-72 font-mono text-sm">
                {r.key}
                {overrideKeys.includes(r.key) && <span className="ml-2 rounded-full bg-secondary px-2 py-0.5 text-[10px] uppercase tracking-wide">per-category</span>}
              </span>
              <Input
                className="flex-1"
                disabled={isLocked}
                defaultValue={JSON.stringify(r.value)}
                onChange={(e) => setEdits({ ...edits, [r.key]: e.target.value })}
                data-testid={`setting-input-${r.key}`}
              />
              {isLocked ? <span className="text-xs text-muted-foreground">locked</span> : (
                <Button size="sm" className="bg-brand-green hover:bg-brand-green-dark" onClick={() => save(r)} data-testid={`setting-save-${r.key}`}>Save</Button>
              )}
            </div>
          );
        })}
      </div>

      <div className="flex flex-col gap-3">
        <div>
          <h3 className="text-sm font-black uppercase tracking-wide">Per-category overrides</h3>
          <p className="text-sm text-muted-foreground">Override an eligible setting for a single category. Falls back to the base value when no override exists.</p>
        </div>
        {overrides.length > 0 && (
          <div className="flex flex-col gap-2">
            {overrides.map((r) => {
              const ek = `${r.key}::${r.category_override_for}`;
              return (
                <div key={ek} className="flex flex-wrap items-center gap-3 rounded-[12px] border border-border bg-card px-4 py-3" data-testid={`override-${r.key}-${r.category_override_for}`}>
                  <span className="w-72 font-mono text-sm">{r.key}<span className="ml-2 text-xs text-brand-green">{catName(r.category_override_for)}</span></span>
                  <Input className="flex-1" defaultValue={JSON.stringify(r.value)} onChange={(e) => setEdits({ ...edits, [ek]: e.target.value })} data-testid={`override-input-${r.key}-${r.category_override_for}`} />
                  <Button size="sm" className="bg-brand-green hover:bg-brand-green-dark" onClick={() => saveOverride(r.key, r.category_override_for, edits[ek] ?? JSON.stringify(r.value))} data-testid={`override-save-${r.key}-${r.category_override_for}`}>Save</Button>
                </div>
              );
            })}
          </div>
        )}
        {overrideKeys.length === 0 ? (
          <p className="text-sm text-muted-foreground">No settings support per-category overrides.</p>
        ) : (
          <div className="flex flex-wrap items-end gap-2 rounded-[12px] border border-dashed border-border px-4 py-3">
            <select className="h-10 rounded-md border border-border bg-background px-2 text-sm" value={ovKey} onChange={(e) => setOvKey(e.target.value)} data-testid="override-key">
              <option value="">Setting…</option>
              {overrideKeys.map((k) => <option key={k} value={k}>{k}</option>)}
            </select>
            <select className="h-10 rounded-md border border-border bg-background px-2 text-sm" value={ovCat} onChange={(e) => setOvCat(e.target.value)} data-testid="override-category">
              <option value="">Category…</option>
              {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <Input className="w-44" placeholder="value (JSON)" value={ovVal} onChange={(e) => setOvVal(e.target.value)} data-testid="override-value" />
            <Button size="sm" className="bg-brand-green hover:bg-brand-green-dark" disabled={!ovKey || !ovCat} onClick={() => saveOverride(ovKey, ovCat, ovVal)} data-testid="override-add">Add override</Button>
          </div>
        )}
      </div>
    </div>
  );
};

const CHANNELS = ["push", "sms", "email"];

const Templates = () => {
  const [rows, setRows] = useState(null);
  const [edits, setEdits] = useState({});
  const [nw, setNw] = useState({ key: "", channel: "push", locale: "en", body: "" });
  const rk = (r) => `${r.key}|${r.channel}|${r.locale}`;
  const load = () => adminApi.get("/admin/message-templates").then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);
  const save = async (r) => {
    try { await adminApi.patch("/admin/message-templates", { key: r.key, channel: r.channel, locale: r.locale, body: edits[rk(r)] ?? r.body }); toast.success("Updated"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const create = async () => {
    if (!nw.key.trim() || !nw.body.trim()) { toast.error("Key and body are required"); return; }
    try {
      await adminApi.patch("/admin/message-templates", { key: nw.key.trim(), channel: nw.channel, locale: nw.locale.trim() || "en", body: nw.body });
      toast.success("Template saved"); setNw({ key: "", channel: "push", locale: "en", body: "" }); load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  if (!rows) return <Loading />;
  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-2">
        {rows.map((r) => (
          <div key={rk(r)} className="rounded-[12px] border border-border bg-card px-4 py-3" data-testid={`tpl-${r.key}-${r.channel}-${r.locale}`}>
            <div className="mb-1 flex items-center gap-2">
              <span className="font-mono text-sm">{r.key}</span>
              <span className="rounded-full bg-secondary px-2 py-0.5 text-xs">{r.channel}</span>
              <span className="rounded-full bg-secondary px-2 py-0.5 text-xs uppercase">{r.locale}</span>
            </div>
            <div className="flex gap-2">
              <Input defaultValue={r.body} onChange={(e) => setEdits({ ...edits, [rk(r)]: e.target.value })} data-testid={`tpl-input-${r.key}-${r.channel}-${r.locale}`} />
              <Button size="sm" className="bg-brand-green hover:bg-brand-green-dark" onClick={() => save(r)} data-testid={`tpl-save-${r.key}-${r.channel}-${r.locale}`}>Save</Button>
            </div>
          </div>
        ))}
      </div>

      <div className="flex flex-col gap-2 rounded-[12px] border border-dashed border-border px-4 py-3">
        <h3 className="text-sm font-black uppercase tracking-wide">New / override template</h3>
        <p className="text-sm text-muted-foreground">Create a new template, or a variant for another channel/locale (key + channel + locale is the unique row).</p>
        <div className="flex flex-wrap items-center gap-2">
          <Input className="w-64" placeholder="key (e.g. payout_sent)" value={nw.key} onChange={(e) => setNw({ ...nw, key: e.target.value })} data-testid="tpl-new-key" />
          <select className="h-10 rounded-md border border-border bg-background px-2 text-sm" value={nw.channel} onChange={(e) => setNw({ ...nw, channel: e.target.value })} data-testid="tpl-new-channel">
            {CHANNELS.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <Input className="w-24" placeholder="locale" value={nw.locale} onChange={(e) => setNw({ ...nw, locale: e.target.value })} data-testid="tpl-new-locale" />
        </div>
        <div className="flex gap-2">
          <Input placeholder="Body — supports {placeholders}" value={nw.body} onChange={(e) => setNw({ ...nw, body: e.target.value })} data-testid="tpl-new-body" />
          <Button size="sm" className="bg-brand-green hover:bg-brand-green-dark" onClick={create} data-testid="tpl-new-save">Save template</Button>
        </div>
      </div>
    </div>
  );
};

const SUBMISSION_FIELDS = ["company_name", "phone", "email", "address", "category", "subcategory"];

const SubmissionCard = ({ s, reload }) => {
  const [fields, setFields] = useState({
    ...s.fields,
    keywords: (s.fields?.keywords || []).join(", "),
  });
  const conf = s.confidences || {};
  const alreadyLive = s.source === "user_added" && !!s.created_branch_id;
  const set = (k, v) => setFields((f) => ({ ...f, [k]: v }));
  const save = async () => {
    try {
      const payload = { ...fields, keywords: (fields.keywords || "").split(",").map((k) => k.trim()).filter(Boolean) };
      await adminApi.patch(`/admin/submissions/${s.id}`, { fields: payload });
      toast.success(alreadyLive ? "Saved to live listing" : "Saved"); reload();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const approve = async () => {
    try {
      await adminApi.post(`/admin/submissions/${s.id}/approve`);
      toast.success(alreadyLive ? "Marked reviewed" : "Approved & outreach sent");
      reload();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const reject = async () => {
    try { await adminApi.post(`/admin/submissions/${s.id}/reject`); toast.success("Rejected"); reload(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const badge = (k) => {
    const v = Number(conf[k] ?? 0);
    const tone = v >= 80 ? "bg-brand-green/15 text-brand-green" : v >= 60 ? "bg-brand-gold/20 text-brand-gold-dark" : "bg-destructive/15 text-destructive";
    return <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${tone}`}>{v}%</span>;
  };
  return (
    <div className="rounded-[12px] border border-border bg-card p-4" data-testid={`submission-${s.id}`}>
      <div className="flex gap-4">
        {s.photo_url && (
          <img src={imgSrc(s.photo_url)} alt="submission" className="h-28 w-28 flex-shrink-0 rounded-[10px] border border-border object-cover" />
        )}
        <div className="flex-1">
          <div className="mb-2 flex flex-wrap items-center gap-2">
            {s.needs_review
              ? <span className="rounded-full bg-brand-gold/20 px-2 py-0.5 text-xs font-bold text-brand-gold-dark">Needs review</span>
              : <span className="rounded-full bg-brand-green/15 px-2 py-0.5 text-xs font-bold text-brand-green">Ready</span>}
            {s.source === "user_added" ? (
              <span className="rounded-full bg-secondary px-2 py-0.5 text-xs font-bold" data-testid={`submission-source-${s.id}`}>User added</span>
            ) : s.source === "photo_submission" || s.photo_url ? (
              <span className="rounded-full bg-secondary px-2 py-0.5 text-xs font-bold">Photo</span>
            ) : null}
            {alreadyLive ? (
              <span className="rounded-full bg-brand-green/15 px-2 py-0.5 text-xs font-bold text-brand-green">Already live</span>
            ) : null}
            {s.submitter_name && <span className="text-xs text-muted-foreground">by {s.submitter_name}</span>}
            {s.created_branch_id ? (
              <a href={`/b/${s.created_branch_id}`} className="text-xs font-medium text-brand-green underline" data-testid={`submission-open-${s.id}`}>
                Open branch
              </a>
            ) : null}
          </div>
          <div className="grid gap-2 sm:grid-cols-2">
            {SUBMISSION_FIELDS.map((k) => (
              <div key={k}>
                <label className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-wide text-muted-foreground">{k.replace("_", " ")} {badge(k)}</label>
                <Input className="mt-0.5 h-8" value={fields[k] || ""} onChange={(e) => set(k, e.target.value)} data-testid={`submission-${s.id}-${k}`} />
              </div>
            ))}
            <div className="sm:col-span-2">
              <label className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">keywords</label>
              <Input className="mt-0.5 h-8" value={fields.keywords || ""} onChange={(e) => set("keywords", e.target.value)} data-testid={`submission-${s.id}-keywords`} />
            </div>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={save} data-testid={`submission-save-${s.id}`}>Save</Button>
            <Button size="sm" className="bg-brand-green hover:bg-brand-green-dark" onClick={approve} data-testid={`submission-approve-${s.id}`}>
              {alreadyLive ? "Mark reviewed" : "Approve"}
            </Button>
            <Button size="sm" variant="outline" onClick={reject} data-testid={`submission-reject-${s.id}`}>Reject</Button>
          </div>
        </div>
      </div>
    </div>
  );
};

const Submissions = () => {
  const [rows, setRows] = useState(null);
  const load = () => adminApi.get("/admin/submissions").then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);
  if (!rows) return <Loading />;
  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm text-muted-foreground">
        Photo and user-added submissions awaiting review. User-added listings are already live — fix fields, mark reviewed, or merge duplicates later.
      </p>
      {rows.length === 0 ? <p className="text-muted-foreground">No pending submissions.</p> : rows.map((s) => <SubmissionCard key={s.id} s={s} reload={load} />)}
    </div>
  );
};

const Verification = ({ canManage, onOpenBranch }) => {
  const [rows, setRows] = useState([]);
  const load = () => adminApi.get("/admin/branches/pending-verification").then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);
  const approve = async (id) => { try { await adminApi.post(`/admin/branches/${id}/approve-verification`); toast.success("Approved"); load(); } catch (e) { toast.error(apiErr(e)); } };
  const reject = async (id) => { try { await adminApi.post(`/admin/branches/${id}/reject-verification`); toast.success("Rejected"); load(); } catch (e) { toast.error(apiErr(e)); } };
  return (
    <Queue rows={rows} empty="No pending verifications." render={(r) => (
      <>
        <button
          type="button"
          className={`flex-1 text-left ${canManage ? "hover:text-brand-green" : ""}`}
          onClick={() => canManage && onOpenBranch?.(r.id)}
          data-testid={`verif-open-${r.id}`}
        >
          <p className="font-semibold">{r.company_name}</p>
          <p className="text-xs text-muted-foreground">{r.address}</p>
        </button>
        <Button size="sm" className="bg-brand-green hover:bg-brand-green-dark" onClick={() => approve(r.id)} data-testid={`approve-verif-${r.id}`}>Approve</Button>
        <Button size="sm" variant="outline" onClick={() => reject(r.id)} data-testid={`reject-verif-${r.id}`}>Reject</Button>
      </>
    )} />
  );
};

const Keywords = () => {
  const [rows, setRows] = useState([]);
  const load = () => adminApi.get("/admin/keywords/pending").then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);
  const act = async (id, action) => { await adminApi.post(`/admin/keywords/${id}/${action}`); load(); };
  return (
    <Queue rows={rows} empty="No pending keywords." render={(r) => (
      <>
        <div className="flex-1">
          <p className="font-semibold">{r.keyword}</p>
          <p className="text-xs text-muted-foreground">
            {r.company_name}
            {r.source ? ` · ${r.source}` : ""}
          </p>
        </div>
        <Button size="sm" className="bg-brand-green hover:bg-brand-green-dark" onClick={() => act(r.id, "approve")} data-testid={`kw-approve-${r.id}`}>Approve</Button>
        <Button size="sm" variant="outline" onClick={() => act(r.id, "reject")} data-testid={`kw-reject-${r.id}`}>Reject</Button>
      </>
    )} />
  );
};

const Disputes = () => {
  const [rows, setRows] = useState([]);
  const load = () => adminApi.get("/admin/disputes").then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);
  const resolve = async (id, upheld) => { await adminApi.post(`/admin/disputes/${id}/resolve`, { upheld }); toast.success("Resolved"); load(); };
  return (
    <Queue rows={rows} empty="No disputes." render={(r) => (
      <>
        <div className="flex-1"><p className="font-semibold">{r.reason}</p><p className="text-xs text-muted-foreground">status: {r.status} · {r.payout ? money(r.payout.amount_cents) : ""}</p></div>
        {r.status === "open" && <>
          <Button size="sm" className="bg-brand-green hover:bg-brand-green-dark" onClick={() => resolve(r.id, true)} data-testid={`dispute-uphold-${r.id}`}>Uphold</Button>
          <Button size="sm" variant="outline" onClick={() => resolve(r.id, false)} data-testid={`dispute-reject-${r.id}`}>Reject</Button>
        </>}
      </>
    )} />
  );
};

const Flags = () => {
  const [rows, setRows] = useState([]);
  const load = () => adminApi.get("/admin/flags").then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);
  const act = async (id, status) => { await adminApi.post(`/admin/flags/${id}`, { status }); load(); };
  return (
    <Queue rows={rows} empty="No flags." render={(r) => (
      <>
        <div className="flex-1">
          <p className="font-semibold">{r.target_type}{r.target_type === "user_phone_collision" ? ` · ${r.reason}` : ""}</p>
          <p className="text-xs text-muted-foreground">
            {r.target_type === "user_phone_collision"
              ? `${(r.user_ids || []).length} users · ${(r.raw_phones || []).join(" / ")} · ${r.status}`
              : `${r.reason} · ${r.status}`}
          </p>
        </div>
        {r.status === "pending" && <>
          <Button size="sm" className="bg-brand-green hover:bg-brand-green-dark" onClick={() => act(r.id, "actioned")} data-testid={`flag-action-${r.id}`}>Action</Button>
          <Button size="sm" variant="outline" onClick={() => act(r.id, "dismissed")} data-testid={`flag-dismiss-${r.id}`}>Dismiss</Button>
        </>}
      </>
    )} />
  );
};

const SubItem = ({ s, reload }) => {
  const [badge, setBadge] = useState(s.badge_label || "");
  const save = async () => {
    try { await adminApi.patch(`/admin/subcategories/${s.id}`, { badge_label: badge.trim() }); toast.success("Badge saved"); reload(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const del = async () => {
    if (!window.confirm(`Delete subcategory "${s.name}"?`)) return;
    try { await adminApi.delete(`/admin/subcategories/${s.id}`); reload(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <div className="flex items-center gap-1.5 rounded-[10px] border border-border bg-secondary px-2 py-1" data-testid={`sub-${s.id}`}>
      <span className="text-xs font-medium">{s.name}</span>
      <Input
        className="h-7 w-32 text-xs"
        placeholder="Badge (≤15)"
        maxLength={15}
        value={badge}
        onChange={(e) => setBadge(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && save()}
        data-testid={`sub-badge-${s.id}`}
      />
      <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={save} data-testid={`sub-badge-save-${s.id}`}>Save</Button>
      <button className="text-muted-foreground hover:text-destructive" onClick={del} data-testid={`sub-delete-${s.id}`}>×</button>
    </div>
  );
};

const CategoryRow = ({ c, reload }) => {
  const [name, setName] = useState(c.name);
  const [grace, setGrace] = useState(c.default_grace_period_days ?? 3);
  const [newSub, setNewSub] = useState("");
  const saveEdit = async () => {
    try { await adminApi.patch(`/admin/categories/${c.id}`, { name, default_grace_period_days: Number(grace) }); toast.success("Updated"); reload(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const del = async () => {
    if (!window.confirm(`Delete category "${c.name}"? Its subcategories are removed too.`)) return;
    try { await adminApi.delete(`/admin/categories/${c.id}`); toast.success("Deleted"); reload(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const addSub = async () => {
    if (!newSub.trim()) return;
    try { await adminApi.post("/admin/subcategories", { category_id: c.id, name: newSub.trim() }); setNewSub(""); reload(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <div className="rounded-[12px] border border-border bg-card px-4 py-3" data-testid={`cat-${c.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <Input className="w-56" value={name} onChange={(e) => setName(e.target.value)} data-testid={`cat-name-${c.id}`} />
        <Input type="number" className="w-28" value={grace} onChange={(e) => setGrace(e.target.value)} data-testid={`cat-grace-${c.id}`} />
        <span className="text-xs text-muted-foreground">grace days</span>
        <Button size="sm" className="bg-brand-green hover:bg-brand-green-dark" onClick={saveEdit} data-testid={`cat-save-${c.id}`}>Save</Button>
        <Button size="sm" variant="outline" onClick={del} data-testid={`cat-delete-${c.id}`}>Delete</Button>
      </div>
      <p className="mt-3 text-xs text-muted-foreground">Subcategories — the badge label is the short pill shown on business cards (falls back to the full name if empty).</p>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        {c.subcategories.map((s) => (
          <SubItem key={s.id} s={s} reload={reload} />
        ))}
        <Input className="h-8 w-40" placeholder="add subcategory" value={newSub} onChange={(e) => setNewSub(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addSub()} data-testid={`sub-add-input-${c.id}`} />
        <Button size="sm" variant="outline" onClick={addSub} data-testid={`sub-add-${c.id}`}>Add sub</Button>
      </div>
    </div>
  );
};

const Categories = () => {
  const [rows, setRows] = useState([]);
  const [name, setName] = useState("");
  const [grace, setGrace] = useState(3);
  const load = () => adminApi.get("/categories").then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);
  const create = async () => {
    if (!name.trim()) return;
    try { await adminApi.post("/admin/categories", { name: name.trim(), default_grace_period_days: Number(grace) }); setName(""); setGrace(3); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <div>
      <div className="mb-4 flex gap-2">
        <Input placeholder="New category" value={name} onChange={(e) => setName(e.target.value)} data-testid="cat-name" />
        <Input type="number" className="w-32" placeholder="grace days" value={grace} onChange={(e) => setGrace(e.target.value)} data-testid="cat-grace" />
        <Button className="bg-brand-green hover:bg-brand-green-dark" onClick={create} data-testid="cat-create">Add</Button>
      </div>
      <div className="flex flex-col gap-2">
        {rows.map((c) => <CategoryRow key={c.id} c={c} reload={load} />)}
      </div>
    </div>
  );
};

const SupportTickets = () => {
  const [rows, setRows] = useState(null);
  const [reply, setReply] = useState({});
  const STATUSES = ["open", "in_progress", "resolved"];
  const load = () => adminApi.get("/admin/support/tickets").then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);
  const act = async (id, patch) => {
    try { await adminApi.post(`/admin/support/tickets/${id}`, patch); toast.success("Updated"); setReply({ ...reply, [id]: "" }); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  if (!rows) return <Loading />;
  if (rows.length === 0) return <p className="text-muted-foreground">No support tickets.</p>;
  return (
    <div className="flex flex-col gap-3">
      {rows.map((t) => (
        <div key={t.id} className="rounded-[12px] border border-border bg-card px-4 py-3" data-testid={`ticket-${t.id}`}>
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <span className="flex-1 font-semibold">{t.subject || "(no subject)"}</span>
            <span className="rounded-full bg-secondary px-2 py-0.5 text-xs">{t.status}</span>
            {STATUSES.filter((s) => s !== t.status).map((s) => (
              <Button key={s} size="sm" variant="outline" onClick={() => act(t.id, { status: s })} data-testid={`ticket-status-${t.id}-${s}`}>{s.replace("_", " ")}</Button>
            ))}
          </div>
          {(t.messages || []).length > 0 && (
            <div className="mb-2 flex flex-col gap-1">
              {t.messages.map((m, i) => (
                <div key={i} className="rounded-md bg-secondary/50 px-3 py-1.5 text-sm"><span className="font-semibold">{m.from || "user"}:</span> {m.body}</div>
              ))}
            </div>
          )}
          <div className="flex gap-2">
            <Input placeholder="Reply…" value={reply[t.id] || ""} onChange={(e) => setReply({ ...reply, [t.id]: e.target.value })} data-testid={`ticket-reply-${t.id}`} />
            <Button size="sm" className="bg-brand-green hover:bg-brand-green-dark" disabled={!reply[t.id]} onClick={() => act(t.id, { message: reply[t.id] })} data-testid={`ticket-send-${t.id}`}>Send</Button>
          </div>
        </div>
      ))}
    </div>
  );
};

const SERVICE_MODES = ["in_store", "delivery", "remote"];
const REWARD_TYPES = ["cash", "discount", "thank_you"];
const DAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

const Field = ({ label, children }) => (
  <div className="flex flex-col gap-1">
    <label className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">{label}</label>
    {children}
  </div>
);

// super_admin: full company/branch editor. Reuses the owner-editable field set
// but writes through the admin route (PATCH /admin/branches/:id) and audit log.
const BranchEditor = ({ branchId, onBack }) => {
  const [f, setF] = useState(null);
  const [meta, setMeta] = useState(null);
  const [saving, setSaving] = useState(false);
  const [cats, setCats] = useState([]);
  const load = () => adminApi.get(`/admin/branches/${branchId}`).then(({ data }) => {
    setMeta(data);
    setF({
      name: data.company_name || "",
      category_id: data.category_id || "",
      subcategory_id: data.subcategory_id || "",
      phone: data.phone || "",
      label: data.label || "",
      logo_url: data.logo_url || "",
      portfolio_image_urls: data.portfolio_image_urls || [],
      about: data.about || "",
      website_url: data.website_url || "",
      address: data.address || "",
      city: data.city || "",
      state: data.state || "",
      postal_code: data.postal_code || "",
      service_modes: data.service_modes || [],
      hours: data.hours || {},
      reward_type: data.reward_type || "cash",
      reward_amount_cents: data.reward_amount_cents ?? 1000,
      reward_discount_pct: data.reward_discount_pct ?? 10,
      latitude: data.latitude ?? null,
      longitude: data.longitude ?? null,
      geocode_source: data.geocode_source || "none",
    });
  }).catch((e) => toast.error(apiErr(e)));
  useEffect(() => { load(); adminApi.get("/categories").then(({ data }) => setCats(data || [])).catch(() => {}); }, [branchId]);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const toggleMode = (m) => setF((s) => ({
    ...s,
    service_modes: s.service_modes.includes(m) ? s.service_modes.filter((x) => x !== m) : [...s.service_modes, m],
  }));
  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        name: f.name,
        category_id: f.category_id || null,
        subcategory_id: f.subcategory_id || "",
        phone: f.phone || null,
        label: f.label,
        logo_url: f.logo_url || null,
        portfolio_image_urls: f.portfolio_image_urls,
        about: f.about,
        website_url: f.website_url,
        address: f.address,
        city: f.city || null,
        state: f.state || null,
        postal_code: f.postal_code || null,
        service_modes: f.service_modes,
        hours: f.hours,
        reward_type: f.reward_type,
        reward_amount_cents: f.reward_type === "cash" ? Number(f.reward_amount_cents) : null,
        reward_discount_pct: f.reward_type === "discount" ? Number(f.reward_discount_pct) : null,
      };
      if (f.geocode_source === "manual" && f.latitude != null && f.longitude != null) {
        payload.latitude = f.latitude;
        payload.longitude = f.longitude;
        payload.geocode_source = "manual";
      }
      const { data } = await adminApi.patch(`/admin/branches/${branchId}`, payload);
      setF((s) => ({
        ...s,
        name: data.company_name || s.name,
        category_id: data.category_id || "",
        subcategory_id: data.subcategory_id || "",
        phone: data.phone || "",
        latitude: data.latitude ?? null,
        longitude: data.longitude ?? null,
        geocode_source: data.geocode_source || "none",
      }));
      setMeta(data);
      toast.success("Saved & logged to audit trail");
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(false); }
  };
  const onPinMove = async ({ lat, lng }) => {
    set("latitude", lat);
    set("longitude", lng);
    set("geocode_source", "manual");
    try {
      const { data } = await adminApi.patch(`/admin/branches/${branchId}`, {
        latitude: lat,
        longitude: lng,
        geocode_source: "manual",
      });
      setF((s) => ({
        ...s,
        latitude: data.latitude,
        longitude: data.longitude,
        geocode_source: data.geocode_source || "manual",
      }));
      setMeta(data);
      toast.success("Map pin saved");
    } catch (e) { toast.error(apiErr(e)); }
  };
  const relocate = async () => {
    try {
      const { data } = await adminApi.post(`/admin/branches/${branchId}/regeocode`);
      setF((s) => ({
        ...s,
        latitude: data.latitude ?? null,
        longitude: data.longitude ?? null,
        geocode_source: data.geocode_source || "geocoded",
        address: data.address || s.address,
      }));
      setMeta(data);
      toast.success("Re-located from address");
    } catch (e) { toast.error(apiErr(e)); }
  };
  if (!f) return <Loading />;
  return (
    <div className="flex flex-col gap-5" data-testid="branch-editor">
      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" onClick={onBack} data-testid="branch-editor-back"><ArrowLeft className="mr-1 h-4 w-4" />Back</Button>
        <div>
          <p className="font-black">{meta?.company_name} <span className="text-sm font-normal text-muted-foreground">{meta?.label}</span></p>
          <p className="font-mono text-[10px] text-muted-foreground">{branchId} · {meta?.verification_status}{meta?.owner_user_id ? "" : " · unclaimed"}</p>
        </div>
      </div>

      {(meta?.quality_flags || []).length > 0 ? (
        <div className="rounded-[14px] border border-amber-500/30 bg-amber-500/5 p-4" data-testid="branch-quality-flags">
          <h3 className="mb-2 text-sm font-black uppercase tracking-wide text-amber-800 dark:text-amber-200">Internal quality flags</h3>
          <p className="mb-2 text-xs text-muted-foreground">These do not change the business&apos;s public payer tier.</p>
          <ul className="space-y-1 text-sm">
            {meta.quality_flags.map((f) => (
              <li key={f.id}>{f.reason} · {(f.created_at || "").slice(0, 10)}</li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className="rounded-[14px] border border-border bg-card p-5">
        <h3 className="mb-3 text-sm font-black uppercase tracking-wide">Profile</h3>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2"><Field label="Business name"><Input value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="be-name" /></Field></div>
          <Field label="Phone"><Input value={f.phone} onChange={(e) => set("phone", e.target.value)} data-testid="be-phone" /></Field>
          <Field label="Branch label"><Input value={f.label} onChange={(e) => set("label", e.target.value)} data-testid="be-label" /></Field>
          <Field label="Category">
            <select className="h-10 w-full rounded-md border border-border bg-background px-2 text-sm" value={f.category_id} onChange={(e) => setF((s) => ({ ...s, category_id: e.target.value, subcategory_id: "" }))} data-testid="be-category">
              <option value="">—</option>
              {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </Field>
          <Field label="Subcategory">
            <select className="h-10 w-full rounded-md border border-border bg-background px-2 text-sm" value={f.subcategory_id} onChange={(e) => set("subcategory_id", e.target.value)} data-testid="be-subcategory">
              <option value="">—</option>
              {(cats.find((c) => c.id === f.category_id)?.subcategories || []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </Field>
          <Field label="Website"><Input value={f.website_url} onChange={(e) => set("website_url", e.target.value)} data-testid="be-website" /></Field>
          <div className="sm:col-span-2"><Field label="Street address"><Input value={f.address} onChange={(e) => set("address", e.target.value)} data-testid="be-address" /></Field></div>
          <Field label="City"><Input value={f.city} onChange={(e) => set("city", e.target.value)} data-testid="be-city" /></Field>
          <Field label="State"><Input value={f.state} onChange={(e) => set("state", e.target.value)} data-testid="be-state" /></Field>
          <Field label="ZIP"><Input value={f.postal_code} onChange={(e) => set("postal_code", e.target.value)} data-testid="be-zip" /></Field>
          <div className="sm:col-span-2">
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
              <Field label="Map location" />
              <Button type="button" variant="outline" size="sm" disabled={!(f.address || f.city || f.postal_code)} onClick={relocate} data-testid="be-relocate-btn">
                Re-locate from address
              </Button>
            </div>
            <p className="mb-2 text-xs text-muted-foreground">
              Drag the pin to set exact coords. Manual pins survive address edits.
              {f.geocode_source === "manual" ? " (manual)" : f.geocode_source === "geocoded" ? " (from address)" : " (not set)"}
            </p>
            <LocationPinMap
              latitude={f.latitude}
              longitude={f.longitude}
              onChange={onPinMove}
              testId="be-location-pin-map"
            />
          </div>
          <div className="sm:col-span-2"><Field label="About"><textarea className="min-h-[80px] rounded-md border border-border bg-background p-2 text-sm" value={f.about} onChange={(e) => set("about", e.target.value)} data-testid="be-about" /></Field></div>
        </div>

        <div className="mt-4">
          <Field label="Logo">
            <div className="flex items-center gap-3">
              {f.logo_url && <img src={imgSrc(f.logo_url)} alt="logo" className="h-14 w-14 rounded-[10px] border border-border object-cover" />}
              <ImageUploader uploader={uploadFileAdmin} label="Upload logo" testid="be-logo-upload" onUploaded={(url) => set("logo_url", url)} />
              {f.logo_url && <Button variant="ghost" size="sm" onClick={() => set("logo_url", "")}>Remove</Button>}
            </div>
          </Field>
        </div>

        <div className="mt-4">
          <Field label="Portfolio photos">
            <div className="flex flex-wrap gap-3">
              {f.portfolio_image_urls.map((url, i) => (
                <div key={i} className="relative">
                  <img src={imgSrc(url)} alt="portfolio" className="h-20 w-20 rounded-[10px] border border-border object-cover" />
                  <button className="absolute -right-2 -top-2 rounded-full bg-destructive p-1 text-white" onClick={() => set("portfolio_image_urls", f.portfolio_image_urls.filter((_, j) => j !== i))} data-testid={`be-portfolio-remove-${i}`}><Trash2 className="h-3 w-3" /></button>
                </div>
              ))}
              <ImageUploader uploader={uploadFileAdmin} label="Add photo" testid="be-portfolio-upload" onUploaded={(url) => set("portfolio_image_urls", [...f.portfolio_image_urls, url])} />
            </div>
          </Field>
        </div>

        <div className="mt-4">
          <Field label="Service modes">
            <div className="flex flex-wrap gap-4">
              {SERVICE_MODES.map((m) => (
                <label key={m} className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={f.service_modes.includes(m)} onChange={() => toggleMode(m)} data-testid={`be-mode-${m}`} />
                  {m.replace("_", " ")}
                </label>
              ))}
            </div>
          </Field>
        </div>

        <div className="mt-4">
          <Field label="Hours">
            <div className="grid gap-2 sm:grid-cols-2">
              {DAYS.map((d) => (
                <div key={d} className="flex items-center gap-2">
                  <span className="w-10 text-xs font-bold uppercase text-muted-foreground">{d}</span>
                  <Input className="h-8" placeholder="9-6 or closed" value={f.hours[d] || ""} onChange={(e) => set("hours", { ...f.hours, [d]: e.target.value })} data-testid={`be-hours-${d}`} />
                </div>
              ))}
            </div>
          </Field>
        </div>
      </div>

      <div className="rounded-[14px] border border-border bg-card p-5">
        <h3 className="mb-3 text-sm font-black uppercase tracking-wide">Reward</h3>
        <div className="grid gap-4 sm:grid-cols-3">
          <Field label="Reward type">
            <select className="h-10 rounded-md border border-border bg-background px-2 text-sm" value={f.reward_type} onChange={(e) => set("reward_type", e.target.value)} data-testid="be-reward-type">
              {REWARD_TYPES.map((t) => <option key={t} value={t}>{t.replace("_", " ")}</option>)}
            </select>
          </Field>
          {f.reward_type === "cash" && (
            <Field label="Amount (USD)">
              <Input type="number" value={(Number(f.reward_amount_cents) / 100).toString()} onChange={(e) => set("reward_amount_cents", Math.round(Number(e.target.value) * 100))} data-testid="be-reward-amount" />
            </Field>
          )}
          {f.reward_type === "discount" && (
            <Field label="Discount %">
              <Input type="number" value={f.reward_discount_pct} onChange={(e) => set("reward_discount_pct", e.target.value)} data-testid="be-reward-discount" />
            </Field>
          )}
        </div>
      </div>

      <Button className="self-start bg-brand-green hover:bg-brand-green-dark" disabled={saving} onClick={save} data-testid="be-save">{saving ? "Saving…" : "Save changes"}</Button>
    </div>
  );
};

// super_admin / support: full user profile. super_admin can edit profile fields
// (audited via PATCH /admin/users/:id) and force OTP reset.
const UserDetail = ({ userId, canManage, onBack, onEditBranch }) => {
  const [d, setD] = useState(null);
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);
  const load = () => adminApi.get(`/admin/users/${userId}`).then(({ data }) => {
    setD(data);
    const u = data.user;
    const prefs = u.notification_prefs || { push: true, sms: true, email: false };
    setF({
      name: u.name || "",
      email: u.email || "",
      phone: u.phone || "",
      tax_info_collected: !!u.tax_info_collected,
      notification_prefs: { push: !!prefs.push, sms: !!prefs.sms, email: !!prefs.email },
    });
  }).catch((e) => toast.error(apiErr(e)));
  useEffect(() => { load(); }, [userId]);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const setPref = (k, v) => setF((s) => ({
    ...s,
    notification_prefs: { ...s.notification_prefs, [k]: v },
  }));
  const forceReset = async () => {
    if (!window.confirm("Invalidate this user's active login (OTP) codes? They'll need to request a fresh code to sign in.")) return;
    try { const { data } = await adminApi.post(`/admin/users/${userId}/force-otp-reset`); toast.success(`Reset done — ${data.invalidated} code(s) invalidated`); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const save = async () => {
    setSaving(true);
    try {
      await adminApi.patch(`/admin/users/${userId}`, {
        name: f.name,
        email: f.email,
        phone: f.phone,
        tax_info_collected: f.tax_info_collected,
        notification_prefs: f.notification_prefs,
      });
      toast.success("Saved & logged to audit trail");
      await load();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(false); }
  };
  if (!d || !f) return <Loading />;
  const u = d.user;
  return (
    <div className="flex flex-col gap-5" data-testid="user-detail">
      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" onClick={onBack} data-testid="user-detail-back"><ArrowLeft className="mr-1 h-4 w-4" />Back</Button>
        <div className="flex-1">
          <p className="font-black">{u.name || "(no name)"}</p>
          <p className="font-mono text-[10px] text-muted-foreground">{userId}</p>
        </div>
        {canManage && <Button variant="outline" size="sm" onClick={forceReset} data-testid="user-force-reset">Force password/OTP reset</Button>}
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="rounded-[14px] border border-border bg-card p-5">
          <h3 className="mb-3 text-sm font-black uppercase tracking-wide">Profile</h3>
          {canManage ? (
            <div className="flex flex-col gap-3">
              <Field label="Name"><Input value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="user-edit-name" /></Field>
              <Field label="Phone (E.164)"><Input value={f.phone} onChange={(e) => set("phone", e.target.value)} data-testid="user-edit-phone" /></Field>
              <Field label="Email"><Input value={f.email} onChange={(e) => set("email", e.target.value)} data-testid="user-edit-email" /></Field>
              <Field label="Notifications">
                <div className="flex flex-wrap gap-4 text-sm">
                  {["push", "sms", "email"].map((k) => (
                    <label key={k} className="flex items-center gap-2">
                      <input type="checkbox" checked={!!f.notification_prefs[k]} onChange={(e) => setPref(k, e.target.checked)} data-testid={`user-edit-pref-${k}`} />
                      {k}
                    </label>
                  ))}
                </div>
              </Field>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={f.tax_info_collected} onChange={(e) => set("tax_info_collected", e.target.checked)} data-testid="user-edit-tax" />
                Tax info collected
              </label>
              <dl className="mt-1 grid grid-cols-3 gap-y-2 border-t border-border pt-3 text-sm">
                <dt className="text-muted-foreground">Joined</dt><dd className="col-span-2 font-medium">{(u.created_at || "").slice(0, 10) || "—"}</dd>
                <dt className="text-muted-foreground">Updated</dt><dd className="col-span-2 font-medium">{(u.updated_at || "").slice(0, 10) || "—"}</dd>
                <dt className="text-muted-foreground">Wallet</dt><dd className="col-span-2 font-medium">{money(d.wallet.available_balance_cents)}</dd>
                <dt className="text-muted-foreground">Payout account</dt><dd className="col-span-2 font-mono text-xs font-medium">{u.payout_account_id || "—"}</dd>
              </dl>
              <Button className="mt-2 self-start bg-brand-green hover:bg-brand-green-dark" disabled={saving} onClick={save} data-testid="user-edit-save">{saving ? "Saving…" : "Save changes"}</Button>
              <p className="text-[11px] text-muted-foreground">Passwords are not stored (accounts use phone + one-time code). Changing phone invalidates old OTP codes. Edits are written to the audit trail.</p>
            </div>
          ) : (
            <>
              <dl className="grid grid-cols-3 gap-y-2 text-sm">
                <dt className="text-muted-foreground">Phone</dt><dd className="col-span-2 font-medium">{u.phone || "—"}</dd>
                <dt className="text-muted-foreground">Email</dt><dd className="col-span-2 font-medium">{u.email || "—"}</dd>
                <dt className="text-muted-foreground">Joined</dt><dd className="col-span-2 font-medium">{(u.created_at || "").slice(0, 10) || "—"}</dd>
                <dt className="text-muted-foreground">Wallet</dt><dd className="col-span-2 font-medium">{money(d.wallet.available_balance_cents)}</dd>
                <dt className="text-muted-foreground">Tax info</dt><dd className="col-span-2 font-medium">{u.tax_info_collected ? "Collected" : "Not collected"}</dd>
                <dt className="text-muted-foreground">Notifications</dt>
                <dd className="col-span-2 font-medium">
                  {["push", "sms", "email"].filter((k) => u.notification_prefs?.[k]).join(", ") || "—"}
                </dd>
                <dt className="text-muted-foreground">Payout account</dt><dd className="col-span-2 font-mono text-xs font-medium">{u.payout_account_id || "—"}</dd>
              </dl>
              <p className="mt-3 text-[11px] text-muted-foreground">Passwords are not stored (accounts use phone + one-time code), so there is nothing to display.</p>
            </>
          )}
        </div>
        <div className="rounded-[14px] border border-border bg-card p-5">
          <h3 className="mb-3 text-sm font-black uppercase tracking-wide">Referral activity</h3>
          <dl className="grid grid-cols-3 gap-y-2 text-sm">
            <dt className="text-muted-foreground">Referrals</dt><dd className="col-span-2 font-medium">{d.stats.referrals_total}</dd>
            <dt className="text-muted-foreground">Conversions</dt><dd className="col-span-2 font-medium">{d.stats.conversions}</dd>
            <dt className="text-muted-foreground">Businesses added</dt><dd className="col-span-2 font-medium">{d.stats.businesses_added}</dd>
            <dt className="text-muted-foreground">Invites sent</dt><dd className="col-span-2 font-medium">{d.stats.invites_sent}</dd>
            <dt className="text-muted-foreground">Earnings</dt><dd className="col-span-2 font-medium">{money(d.stats.earnings_cents)}</dd>
            {d.trust ? (
              <>
                <dt className="text-muted-foreground">Trust tier</dt>
                <dd className="col-span-2 font-medium" data-testid="user-trust-tier">{d.trust.tier?.label || "—"} ({d.trust.points} pts)</dd>
              </>
            ) : null}
          </dl>
        </div>
      </div>

      <div className="rounded-[14px] border border-border bg-card p-5">
        <h3 className="mb-3 text-sm font-black uppercase tracking-wide">Owned businesses ({d.owned_businesses.length})</h3>
        {d.owned_businesses.length === 0 ? <p className="text-sm text-muted-foreground">None.</p> : (
          <div className="flex flex-col gap-2">
            {d.owned_businesses.map((b) => (
              <button
                key={b.branch_id}
                type="button"
                className={`rounded-[10px] border border-border px-3 py-2 text-left text-sm ${canManage ? "hover:border-brand-green" : ""}`}
                onClick={() => canManage && onEditBranch?.(b.branch_id)}
                data-testid={`user-owned-${b.branch_id}`}
              >
                <p className="font-semibold">{b.company_name} <span className="text-xs text-muted-foreground">{b.label}</span> <span className="rounded-full bg-secondary px-2 py-0.5 text-[10px]">{b.verification_status}</span>{canManage ? <span className="ml-2 text-[10px] text-brand-green">Edit</span> : null}</p>
                <p className="text-xs text-muted-foreground">{b.address}{b.phone ? ` · ${b.phone}` : ""}</p>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="rounded-[14px] border border-border bg-card p-5">
        <h3 className="mb-3 text-sm font-black uppercase tracking-wide">Referral links ({(d.referral_links || []).length})</h3>
        {(d.referral_links || []).length === 0 ? <p className="text-sm text-muted-foreground">None.</p> : (
          <div className="flex flex-col gap-1">
            {d.referral_links.map((l) => (
              <div key={l.id} className="flex justify-between border-b border-border py-1 text-sm last:border-0">
                <span className="truncate font-mono text-xs text-muted-foreground">{l.id}</span>
                <span className="text-xs text-muted-foreground">{(l.created_at || "").slice(0, 10)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="rounded-[14px] border border-border bg-card p-5">
        <h3 className="mb-3 text-sm font-black uppercase tracking-wide">Wallet transactions ({d.wallet_transactions.length})</h3>
        {d.wallet_transactions.length === 0 ? <p className="text-sm text-muted-foreground">None.</p> : (
          <div className="flex flex-col gap-1">
            {d.wallet_transactions.map((t) => (
              <div key={t.id} className="flex justify-between border-b border-border py-1 text-sm last:border-0">
                <span className="text-muted-foreground">{(t.created_at || "").slice(0, 10)} · {t.type}</span>
                <span className="font-medium">{money(t.amount_cents)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const Lookup = ({ role, intent, onIntentConsumed }) => {
  const canManage = role === "super_admin";
  const [q, setQ] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editBranch, setEditBranch] = useState(null);
  const [viewUser, setViewUser] = useState(null);
  const [focus, setFocus] = useState("users");

  const loadUsers = async (term = "") => {
    setLoading(true);
    try {
      const { data } = await adminApi.get("/admin/users/lookup", { params: term.trim() ? { q: term } : {} });
      setResult(data);
      setFocus("users");
    } catch (e) { toast.error(apiErr(e)); }
    finally { setLoading(false); }
  };

  const loadBranches = async ({ term = "", verifiedOnly = false } = {}) => {
    setLoading(true);
    try {
      const params = { limit: 50 };
      if (term.trim()) params.q = term.trim();
      if (verifiedOnly) params.verification_status = "verified";
      const { data } = await adminApi.get("/admin/branches", { params });
      setResult({ users: [], branches: data.branches || [] });
      setFocus("branches");
    } catch (e) { toast.error(apiErr(e)); }
    finally { setLoading(false); }
  };

  const search = async (term = q) => {
    if (focus === "branches" && !term.trim()) {
      await loadBranches({ verifiedOnly: false });
      return;
    }
    await loadUsers(term);
  };

  useEffect(() => {
    if (!intent) {
      // Initial browse only — don't reload when intent is cleared after openUser/openBranch.
      if (result == null && !editBranch && !viewUser) loadUsers("");
      return;
    }
    const run = async () => {
      if (intent.openBranch) {
        setEditBranch(intent.openBranch);
        onIntentConsumed?.();
        return;
      }
      if (intent.openUser) {
        setViewUser(intent.openUser);
        onIntentConsumed?.();
        return;
      }
      if (intent.focus === "branches") {
        setQ("");
        await loadBranches({ verifiedOnly: !!intent.verifiedOnly });
      } else {
        setQ("");
        await loadUsers("");
      }
      onIntentConsumed?.();
    };
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intent]);

  if (editBranch) {
    return (
      <BranchEditor
        branchId={editBranch}
        onBack={() => {
          setEditBranch(null);
          if (focus === "branches") loadBranches({ term: q, verifiedOnly: false });
          else if (viewUser) { /* stay returning to user after branch edit */ }
          else loadUsers(q);
        }}
      />
    );
  }
  if (viewUser) {
    return (
      <UserDetail
        userId={viewUser}
        canManage={canManage}
        onBack={() => setViewUser(null)}
        onEditBranch={(branchId) => setEditBranch(branchId)}
      />
    );
  }
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap gap-2">
        <Input className="min-w-[220px] flex-1" placeholder="Search users or businesses (name, phone, email, address)…" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()} data-testid="lookup-q" />
        <Button className="bg-brand-green hover:bg-brand-green-dark" onClick={() => search()} data-testid="lookup-search">Search</Button>
        <Button variant="outline" onClick={() => { setQ(""); loadUsers(""); }} data-testid="lookup-browse-all">All users</Button>
        <Button variant="outline" onClick={() => { setQ(""); loadBranches({}); }} data-testid="lookup-browse-branches">All businesses</Button>
      </div>
      <p className="text-xs text-muted-foreground">Click a user to view/edit their profile. Click a business to open the branch editor. Empty search browses recent records.</p>
      {loading && <Loading />}
      {result && (
        <div className="grid gap-6 md:grid-cols-2">
          <div>
            <h3 className="mb-2 text-sm font-black uppercase tracking-wide">Users ({result.users.length})</h3>
            <div className="flex flex-col gap-2">
              {result.users.length === 0 ? <p className="text-sm text-muted-foreground">No users.</p> : result.users.map((u) => (
                <button key={u.id} className="rounded-[12px] border border-border bg-card px-4 py-2 text-left hover:border-brand-green" onClick={() => setViewUser(u.id)} data-testid={`lookup-user-${u.id}`}>
                  <p className="font-semibold">{u.name || "(no name)"}</p>
                  <p className="text-xs text-muted-foreground">{u.phone}{u.email ? ` · ${u.email}` : ""}</p>
                </button>
              ))}
            </div>
          </div>
          <div>
            <h3 className="mb-2 text-sm font-black uppercase tracking-wide">Branches ({result.branches.length})</h3>
            <div className="flex flex-col gap-2">
              {result.branches.length === 0 ? <p className="text-sm text-muted-foreground">No branches. Try “All businesses” or search.</p> : result.branches.map((b) => (
                <button
                  key={b.id}
                  type="button"
                  className={`flex items-start gap-2 rounded-[12px] border border-border bg-card px-4 py-2 text-left ${canManage ? "hover:border-brand-green" : ""}`}
                  onClick={() => {
                    if (canManage) setEditBranch(b.id);
                    else toast.message("Branch editing requires super_admin");
                  }}
                  data-testid={`lookup-branch-${b.id}`}
                >
                  <div className="flex-1">
                    <p className="font-semibold">{b.company_name} <span className="text-xs text-muted-foreground">{b.label}</span> {b.verification_status ? <span className="rounded-full bg-secondary px-2 py-0.5 text-[10px]">{b.verification_status}</span> : null}</p>
                    <p className="text-xs text-muted-foreground">{b.address}{b.phone ? ` · ${b.phone}` : ""}</p>
                    <p className="font-mono text-[10px] text-muted-foreground">{b.id}</p>
                  </div>
                  {canManage && <span className="mt-1 inline-flex items-center text-xs font-medium text-brand-green"><Pencil className="mr-1 h-3 w-3" />Edit</span>}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Audit = ({ onNavigate }) => {
  const [rows, setRows] = useState(null);
  const [target, setTarget] = useState("");
  const load = () => adminApi.get("/admin/audit-log", { params: target.trim() ? { target_id: target.trim() } : {} }).then(({ data }) => setRows(data)).catch((e) => toast.error(apiErr(e)));
  useEffect(() => { load(); }, []);
  const fmtVal = (v) => {
    if (v === null || v === undefined) return "—";
    if (typeof v === "object") return JSON.stringify(v);
    return String(v);
  };
  const openTarget = (r) => {
    if (!r?.target_id) return;
    if (r.target_type === "user") onNavigate?.("Lookup", { openUser: r.target_id });
    else if (r.target_type === "branch") onNavigate?.("Lookup", { openBranch: r.target_id });
  };
  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-muted-foreground">Append-only log of every privileged admin change (profile edits, force resets). Click a row to open the target user or branch.</p>
      <div className="flex gap-2">
        <Input placeholder="Filter by target id (user/branch)…" value={target} onChange={(e) => setTarget(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} data-testid="audit-target" />
        <Button className="bg-brand-green hover:bg-brand-green-dark" onClick={load} data-testid="audit-filter">Filter</Button>
      </div>
      {!rows ? <Loading /> : rows.length === 0 ? <p className="text-muted-foreground">No audit entries.</p> : (
        <div className="flex flex-col gap-2">
          {rows.map((r) => (
            <button
              key={r.id}
              type="button"
              className="rounded-[12px] border border-border bg-card px-4 py-3 text-left hover:border-brand-green"
              onClick={() => openTarget(r)}
              data-testid={`audit-${r.id}`}
            >
              <div className="mb-1 flex flex-wrap items-center gap-2">
                <span className="rounded-full bg-brand-green/15 px-2 py-0.5 text-xs font-bold text-brand-green">{r.action}</span>
                <span className="text-sm font-semibold">{r.admin_email || r.admin_id}</span>
                <span className="text-xs text-muted-foreground">{(r.created_at || "").replace("T", " ").slice(0, 19)}</span>
              </div>
              <p className="text-xs text-muted-foreground">{r.target_type} · <span className="font-mono">{r.target_id}</span>{r.meta?.company_name ? ` · ${r.meta.company_name}` : ""}</p>
              {r.changes && Object.keys(r.changes).length > 0 && (
                <div className="mt-2 flex flex-col gap-1">
                  {Object.entries(r.changes).map(([k, ch]) => (
                    <div key={k} className="text-xs">
                      <span className="font-bold">{k}:</span> <span className="text-destructive line-through">{fmtVal(ch.before)}</span> <span className="text-muted-foreground">→</span> <span className="text-brand-green">{fmtVal(ch.after)}</span>
                    </div>
                  ))}
                </div>
              )}
              {r.meta?.note && <p className="mt-1 text-[11px] text-muted-foreground">{r.meta.note}</p>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const Merge = () => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [keep, setKeep] = useState(null);
  const [mergeB, setMergeB] = useState(null);
  const search = async () => {
    if (!q.trim()) return;
    try { const { data } = await adminApi.get("/admin/users/lookup", { params: { q } }); setResults(data.branches); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const doMerge = async () => {
    if (!keep || !mergeB || keep.id === mergeB.id) { toast.error("Pick two different branches"); return; }
    if (!window.confirm(`Merge "${mergeB.company_name}" INTO "${keep.company_name}"?\nThis deletes the merged branch and moves all of its records to the survivor.`)) return;
    try { await adminApi.post("/admin/branches/merge", { keep_id: keep.id, merge_id: mergeB.id }); toast.success("Merged"); setKeep(null); setMergeB(null); setResults([]); setQ(""); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const Pick = ({ b }) => (
    <div className="rounded-[12px] border border-border bg-card px-4 py-2" data-testid={`merge-result-${b.id}`}>
      <p className="font-semibold">{b.company_name} <span className="text-xs text-muted-foreground">{b.label}</span></p>
      <p className="text-xs text-muted-foreground">{b.address}</p>
      <div className="mt-1 flex gap-2">
        <Button size="sm" variant="outline" onClick={() => setKeep(b)} data-testid={`merge-keep-${b.id}`}>Keep</Button>
        <Button size="sm" variant="outline" onClick={() => setMergeB(b)} data-testid={`merge-into-${b.id}`}>Merge this</Button>
      </div>
    </div>
  );
  return (
    <div className="flex flex-col gap-4">
      <div className="rounded-[12px] border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm">
        <span className="font-bold">Heads up:</span> branch merge is destructive and is <span className="font-bold">disabled</span> until an operator arms <span className="font-mono">ADMIN_MERGE_ENABLED</span>. Submitting will return “disabled pending review” until then.
      </div>
      <div className="flex gap-2">
        <Input placeholder="Find duplicate businesses…" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()} data-testid="merge-q" />
        <Button className="bg-brand-green hover:bg-brand-green-dark" onClick={search} data-testid="merge-search">Search</Button>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="rounded-[12px] border border-border p-3"><p className="mb-1 text-xs font-bold uppercase text-brand-green">Survivor (keep)</p>{keep ? <p className="font-semibold">{keep.company_name} — {keep.label}</p> : <p className="text-sm text-muted-foreground">none selected</p>}</div>
        <div className="rounded-[12px] border border-border p-3"><p className="mb-1 text-xs font-bold uppercase text-destructive">Merged (deleted)</p>{mergeB ? <p className="font-semibold">{mergeB.company_name} — {mergeB.label}</p> : <p className="text-sm text-muted-foreground">none selected</p>}</div>
      </div>
      <Button className="self-start bg-brand-green hover:bg-brand-green-dark" disabled={!keep || !mergeB} onClick={doMerge} data-testid="merge-submit">Merge branches</Button>
      <div className="flex flex-col gap-2">
        {results.map((b) => <Pick key={b.id} b={b} />)}
      </div>
    </div>
  );
};

const Queue = ({ rows, empty, render }) => (
  <div className="flex flex-col gap-2">
    {rows.length === 0 ? <p className="text-muted-foreground">{empty}</p> : rows.map((r) => (
      <div key={r.id} className="flex items-center gap-3 rounded-[12px] border border-border bg-card px-4 py-3">{render(r)}</div>
    ))}
  </div>
);

export default function AdminApp() {
  const [authed, setAuthed] = useState(null);
  const [role, setRole] = useState(null);
  const [tab, setTab] = useState("Overview");
  const [lookupIntent, setLookupIntent] = useState(null);
  const [overlayBranch, setOverlayBranch] = useState(null);

  const check = async () => {
    if (!localStorage.getItem("rl_admin_token")) { setAuthed(false); return; }
    try { const { data } = await adminApi.get("/auth/admin/me"); setRole(data.role); setAuthed(true); } catch { setAuthed(false); }
  };
  useEffect(() => { check(); }, []);

  const navigate = (target, opts = {}) => {
    if (target === "Lookup") {
      setLookupIntent({
        focus: opts.focus || "users",
        verifiedOnly: !!opts.verifiedOnly,
        openUser: opts.openUser || null,
        openBranch: opts.openBranch || null,
        nonce: Date.now(),
      });
    }
    setTab(target);
  };

  if (authed === null) return <Loading />;
  if (!authed) return <AdminLogin onLogin={check} />;

  const visibleTabs = TABS.filter((t) => (TAB_ROLES[t] || []).includes(role));
  const activeTab = visibleTabs.includes(tab) ? tab : (visibleTabs[0] || "Overview");
  const canManage = role === "super_admin";

  if (overlayBranch) {
    return (
      <div className="min-h-screen">
        <header className="sticky top-0 z-40 border-b border-border bg-background/80 px-4 py-3 backdrop-blur-xl">
          <div className="mx-auto flex max-w-6xl items-center justify-between">
            <div className="flex items-center gap-3"><Logo /><span className="rounded-full bg-brand-green/15 px-2 py-0.5 text-xs font-bold text-brand-green">ADMIN</span>{role && <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{role.replace("_", " ")}</span>}</div>
            <Button variant="outline" size="sm" onClick={() => { localStorage.removeItem("rl_admin_token"); setRole(null); setAuthed(false); }} data-testid="admin-logout">Log out</Button>
          </div>
        </header>
        <div className="mx-auto max-w-6xl px-4 py-6">
          <BranchEditor branchId={overlayBranch} onBack={() => setOverlayBranch(null)} />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-40 border-b border-border bg-background/80 px-4 py-3 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between">
          <div className="flex items-center gap-3"><Logo /><span className="rounded-full bg-brand-green/15 px-2 py-0.5 text-xs font-bold text-brand-green">ADMIN</span>{role && <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{role.replace("_", " ")}</span>}</div>
          <Button variant="outline" size="sm" onClick={() => { localStorage.removeItem("rl_admin_token"); setRole(null); setAuthed(false); }} data-testid="admin-logout">Log out</Button>
        </div>
      </header>
      <div className="mx-auto max-w-6xl px-4 py-6">
        <div className="mb-6 flex flex-wrap gap-1 border-b border-border">
          {visibleTabs.map((t) => (
            <button key={t} onClick={() => setTab(t)} data-testid={`admin-tab-${t.toLowerCase()}`}
              className={`border-b-2 px-3 py-2 text-sm font-medium ${activeTab === t ? "border-brand-green text-brand-green" : "border-transparent text-muted-foreground hover:text-foreground"}`}>{t}</button>
          ))}
        </div>
        {activeTab === "Overview" && <Overview onNavigate={navigate} />}
        {activeTab === "Analytics" && <Analytics onNavigate={navigate} />}
        {activeTab === "Settings" && <Settings />}
        {activeTab === "Templates" && <Templates />}
        {activeTab === "Submissions" && <Submissions />}
        {activeTab === "Verification" && <Verification canManage={canManage} onOpenBranch={setOverlayBranch} />}
        {activeTab === "Keywords" && <Keywords />}
        {activeTab === "Disputes" && <Disputes />}
        {activeTab === "Flags" && <Flags />}
        {activeTab === "Categories" && <Categories />}
        {activeTab === "Support" && <SupportTickets />}
        {activeTab === "Lookup" && (
          <Lookup
            role={role}
            intent={lookupIntent}
            onIntentConsumed={() => setLookupIntent(null)}
          />
        )}
        {activeTab === "Audit" && <Audit onNavigate={navigate} />}
        {activeTab === "Merge" && <Merge />}
      </div>
    </div>
  );
}
